import { toast } from "sonner";

/**
 * Build a Google review URL from a place_id when no explicit review URL is set.
 * This is the most reliable fallback when Business Profile short link is missing.
 */
export function buildReviewUrlFromPlaceId(placeId) {
  if (!placeId) return null;
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

/**
 * Detect if the current browser is an in-app WebView (Instagram, Facebook, TikTok, LinkedIn, Messenger).
 * Google's login + review form often refuse to work inside these and just hang on a blank screen.
 */
export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Instagram|FBAN|FBAV|FB_IAB|Messenger|TikTok|Line\//i.test(ua)
    || /LinkedInApp/i.test(ua)
    || (/; wv\)/i.test(ua));
}

/**
 * Normalise a Google review URL to a format that reliably opens the review form.
 * Accepts:
 *   - g.page/r/XXXX               → https://g.page/r/XXXX/review
 *   - g.page/r/XXXX/review        → kept
 *   - search.google.com/local/writereview?placeid=…  → kept
 *   - maps.google.com/…?cid=…      → kept (best effort)
 *   - everything else              → returned as is
 */
export function normalizeGoogleReviewUrl(raw) {
  if (!raw) return null;
  let url = raw.trim();
  // Ensure protocol
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  // g.page/r/XXX without /review → add /review
  const m = url.match(/^https?:\/\/g\.page\/r\/([A-Za-z0-9_-]+)\/?$/i);
  if (m) url = `https://g.page/r/${m[1]}/review`;
  return url;
}

/**
 * Opens the Google review URL safely.
 * - Normalises the URL first.
 * - Detects in-app browsers and shows a helpful toast instead of a blank page.
 * Returns true if navigation was attempted, false if blocked for user guidance.
 */
export function openGoogleReview(rawUrl, fallbackSearchUrl = null) {
  const url = normalizeGoogleReviewUrl(rawUrl);
  if (!url) {
    toast.error("Aucun lien Google configuré.");
    return false;
  }
  if (isInAppBrowser()) {
    // Copy to clipboard + guidance
    try { navigator.clipboard?.writeText(url); } catch (_e) {}
    toast.info(
      "Ouvrez Safari ou Chrome pour laisser votre avis Google. Le lien a été copié.",
      { duration: 6000 }
    );
    // Attempt intent:// on Android to force Chrome
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) {
      const intent = `intent://${url.replace(/^https?:\/\//, "")}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intent;
      return true;
    }
    return false;
  }
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}
